import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatDate, statusTone } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = ["To Do", "In Progress", "Completed", "Blocked"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export default function ManagementTasks() {
  const { user } = useAuth();
  const isSA = user.role === "SuperAdmin";
  const [items, setItems] = useState([]);
  const [managers, setManagers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: "", project_id: "", description: "", assignee_id: "", priority: "Medium", start_date: "", due_date: "", category: "General", recurring: false });

  const load = async () => {
    const { data } = await api.get("/management-tasks"); setItems(data);
    if (isSA) {
      const u = await api.get("/users"); setManagers(u.data.filter((x) => x.role === "Manager"));
      const p = await api.get("/projects"); setProjects(p.data);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/management-tasks", form);
      setNewOpen(false); setForm({ title: "", project_id: "", description: "", assignee_id: "", priority: "Medium", start_date: "", due_date: "", category: "General", recurring: false });
      load(); toast.success("Task created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setStatus = async (t, status) => {
    try { await api.patch(`/management-tasks/${t.id}`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <PageHeader
        title="Management Tasks"
        description={isSA ? "Assign, prioritise and track manager workload." : "Tasks assigned to you."}
        actions={isSA && <button data-testid="new-task-btn" onClick={() => setNewOpen(true)} className="btn-primary">+ Create Management Task</button>}
      />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {STATUSES.map((st) => (
            <div key={st} className="bg-white rounded-2xl border border-black/5">
              <div className="px-4 py-3 border-b border-black/5 label-caps flex justify-between"><span>{st}</span><span className="font-mono-tight text-neutral-500">{items.filter((t) => t.status === st).length}</span></div>
              <div className="p-3 space-y-2 min-h-[200px]">
                {items.filter((t) => t.status === st).map((t) => (
                  <div key={t.id} data-testid={`task-card-${t.id}`} className="border border-black/5 rounded-lg p-3 bg-cream/40">
                    <div className="flex justify-between items-start gap-2">
                      <div className="font-semibold text-sm">{t.title}</div>
                      <span className={`px-1.5 py-0 text-[10px] font-mono-tight uppercase rounded ${t.priority === "Urgent" ? "bg-[#B02A00] text-white" : t.priority === "High" ? "bg-gold text-ink" : "bg-royal-600 text-cream"}`}>{t.priority}</span>
                    </div>
                    <div className="text-xs text-ink/60 mt-1">Due {formatDate(t.due_date)}</div>
                    {(isSA || t.assignee_id === user.id) && (
                      <select data-testid={`task-status-${t.id}`} value={t.status} onChange={(e) => setStatus(t, e.target.value)} className="mt-2 w-full text-xs border border-black/10 rounded px-2 py-1 bg-white font-mono-tight">
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-[1.25rem]">
          <DialogHeader><DialogTitle className="font-display text-2xl">Create Management Task</DialogTitle><DialogDescription>Assign a task to a manager with priority and due date.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><label className="label-caps block mb-1">Title</label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Project</label>
                <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">—</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div><label className="label-caps block mb-1">Assignee</label>
                <select required value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Select manager</option>
                  {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div><label className="label-caps block mb-1">Description</label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg border-black/15" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label-caps block mb-1">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm bg-white">
                  {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div><label className="label-caps block mb-1">Start</label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-lg border-black/15" /></div>
              <div><label className="label-caps block mb-1">Due</label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-lg border-black/15" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Category</label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-lg border-black/15" /></div>
              <label className="flex items-end gap-2 text-sm"><input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} /> Recurring monthly</label>
            </div>
            <button data-testid="submit-task" type="submit" className="btn-primary w-full">Create Task</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
